import { useRef, useState } from "react";
import { Redirect } from "wouter";
import { getMerchantToken } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";

/**
 * Phase 14 — Marketplace Import.
 *
 * Drag-drop or click-to-pick a single .xlsx export from Shopee /
 * Lazada / TikTok. We POST it as `multipart/form-data` to
 * `/tenant/marketplace/upload`; the server auto-detects the platform
 * (sheet name + header keywords) but the merchant can pre-select if
 * they renamed the file. The summary card after a successful upload
 * shows totals + a "Download PDF" button that calls
 * `/tenant/marketplace/generate-pdf` and saves the response as a Blob.
 */

interface UploadResponse {
  platform: "SHOPEE" | "LAZADA" | "TIKTOK" | "OTHER";
  totalRows: number;
  ordersAccepted: number;
  codesGenerated: number;
  skipped: {
    duplicate: number;
    cancelled: number;
    refunded: number;
    invalid: number;
  };
  warnings: string[];
  generatedCodes: string[];
}

const PLATFORMS: Array<UploadResponse["platform"]> = [
  "SHOPEE",
  "LAZADA",
  "TIKTOK",
];

export function MerchantMarketplaceImportPage() {
  if (!getMerchantToken()) return <Redirect to="/merchant/login" />;

  const [platform, setPlatform] = useState<UploadResponse["platform"] | "">("");
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function submit() {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (platform) fd.append("platform", platform);
      const token = getMerchantToken();
      const res = await fetch("/api/v1/tenant/marketplace/upload", {
        method: "POST",
        headers: token ? { authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const json = (await res.json()) as
        | { data: UploadResponse }
        | { error: { code: string; message: string } };
      if (!res.ok || !("data" in json)) {
        const msg =
          "error" in json
            ? json.error.message
            : `Upload failed (${res.status}).`;
        setError(msg);
      } else {
        setResult(json.data);
      }
    } catch (e) {
      setError(`Upload failed: ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  }

  async function downloadPdf(codes: string[]) {
    setDownloading(true);
    try {
      // The endpoint returns the PDF binary directly (not the JSON
      // envelope), so we use raw `fetch` here to stream the bytes
      // into a Blob and trigger the browser's "save as" dialog.
      const token = getMerchantToken();
      const res = await fetch("/api/v1/tenant/marketplace/generate-pdf", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ codes }),
      });
      if (!res.ok) {
        setError(`PDF download failed (${res.status}).`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orbit-claim-codes-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <main className="mx-auto max-w-4xl p-4 md:p-6">
      <MerchantNav current="marketplace" />

      <h1 className="mt-6 text-2xl font-bold text-navy">Marketplace import</h1>
      <p className="mt-1 text-sm text-ink-dark">
        Upload your daily Shopee, Lazada, or TikTok orders export. We'll
        generate one claim code per order and a printable PDF you can
        slip into the package.
      </p>

      {/* Platform hint (optional) */}
      <div className="mt-6">
        <label className="label">Platform (optional — we'll auto-detect)</label>
        <div className="mt-1 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setPlatform("")}
            className={`rounded-full px-3 py-1 text-sm border ${
              platform === ""
                ? "bg-navy text-white border-navy"
                : "bg-white text-ink-dark border-stone-300"
            }`}
          >
            Auto
          </button>
          {PLATFORMS.map((p) => (
            <button
              type="button"
              key={p}
              onClick={() => setPlatform(p)}
              className={`rounded-full px-3 py-1 text-sm border ${
                platform === p
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-ink-dark border-stone-300"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Drag-drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) setFile(f);
        }}
        onClick={() => inputRef.current?.click()}
        className={`mt-4 cursor-pointer rounded-lg border-2 border-dashed p-8 text-center ${
          dragOver
            ? "border-navy bg-navy/5"
            : "border-stone-300 bg-white hover:border-navy"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) setFile(f);
          }}
        />
        <p className="text-ink-dark">
          {file
            ? `Selected: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`
            : "Drop your .xlsx here, or click to choose a file."}
        </p>
        <p className="mt-1 text-xs text-stone-500">
          5 MB max. One workbook per upload.
        </p>
      </div>

      <button
        type="button"
        disabled={!file || uploading}
        onClick={submit}
        className="mt-4 rounded bg-navy px-5 py-2 font-semibold text-white disabled:bg-stone-300"
      >
        {uploading ? "Uploading…" : "Upload & generate codes"}
      </button>

      {error && (
        <div className="mt-4 rounded bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-6 rounded border border-stone-200 bg-white p-4">
          <h2 className="text-lg font-semibold text-navy">
            {result.platform} — {result.codesGenerated} codes generated
          </h2>
          <ul className="mt-2 grid grid-cols-2 gap-y-1 text-sm text-ink-dark md:grid-cols-3">
            <li>Total rows seen: <strong>{result.totalRows}</strong></li>
            <li>Orders accepted: <strong>{result.ordersAccepted}</strong></li>
            <li>Codes generated: <strong>{result.codesGenerated}</strong></li>
            <li>Duplicate skipped: {result.skipped.duplicate}</li>
            <li>Cancelled: {result.skipped.cancelled}</li>
            <li>Refunded: {result.skipped.refunded}</li>
            <li>Invalid: {result.skipped.invalid}</li>
          </ul>
          {result.warnings.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-sm text-amber-700">
                {result.warnings.length} warning(s)
              </summary>
              <ul className="mt-1 list-disc pl-5 text-xs text-stone-600">
                {result.warnings.slice(0, 20).map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
                {result.warnings.length > 20 && (
                  <li>…and {result.warnings.length - 20} more</li>
                )}
              </ul>
            </details>
          )}
          {result.generatedCodes.length > 0 && (
            <button
              type="button"
              onClick={() => downloadPdf(result.generatedCodes)}
              disabled={downloading}
              className="mt-4 rounded border border-navy px-4 py-2 text-sm font-semibold text-navy hover:bg-navy hover:text-white disabled:opacity-50"
            >
              {downloading ? "Building PDF…" : "Download printable PDF"}
            </button>
          )}
        </div>
      )}
    </main>
  );
}
